import { get } from "lodash"
import { getContributorDescriptors, getQueryParameters, getSimulateActionsNode, getUsageParameters, getIncludeNotHeldParameter, getForecastSecuritiesParameter, getForecastIdParameter, getAnalyticsOutputParameter } from "./helpers"

function forecastFragment(forecastIdParameter) {
  if (forecastIdParameter) { return "custom(id: $forecastId)" }

  return "impliedReturns (riskFactors: MARKET)"
}

export function forecastQuery(forecastAndHorizon, options = {}) {
  const forecastIdParameter = getForecastIdParameter(forecastAndHorizon, options)
  const analyticsOuputParameter = getAnalyticsOutputParameter(options)
  return `query ($horizon: Int! ${getQueryParameters([analyticsOuputParameter, forecastIdParameter])}) {
    model (id: $modelId) {
      simulation: ${getSimulateActionsNode([analyticsOuputParameter])} {
        ${forecastQueryFragment(forecastIdParameter, options)}
      }
    }
  }`
}

// QueryFragments are called by the rebalanceQuery so they need to include OpPro checks which are normally done before even calling the query
export function forecastQueryFragment(forecastIdParameter, options) {
  const { isOpPro } = options ?? {}

  // Forecast contributors need to be included if a forecast is present or for implied returns (no forecast; enterprise plan)
  if (!forecastIdParameter && isOpPro) { return "" }
  return `forecast (horizon: $horizon) {
    forecast: ${forecastFragment(forecastIdParameter)} {
      horizon
      total
      equities {
        id {
          ticker
          mic
          modelProviderId
        }
        expectedPercentReturn
      }
      swaps { id expectedPercentReturn }
      indexes { id expectedPercentReturn }
    }
  }`
}

export function forecastContributorsQuery(forecastAndHorizon, options = {}) {
  const { isOpPro } = options ?? {}
  const forecast = get(forecastAndHorizon, "forecast")
  if (!forecast && isOpPro) { return "" }

  const includeNotHeldParameter = getIncludeNotHeldParameter(options)
  const forecastIdParameter = getForecastIdParameter(forecastAndHorizon, options)
  const forecastSecuritiesParameter = forecastIdParameter ? null : getForecastSecuritiesParameter(options)
  const analyticsOuputParameter = getAnalyticsOutputParameter(options)

  return `query ($horizon: Int! ${getQueryParameters([analyticsOuputParameter, includeNotHeldParameter, forecastSecuritiesParameter, forecastIdParameter])}) {
    model (id: $modelId) {
      simulation: ${getSimulateActionsNode([analyticsOuputParameter])} {
        ${forecastContributorsQueryFragment([includeNotHeldParameter, forecastSecuritiesParameter, forecastIdParameter])}
      }
    }
  }`
}

export function forecastContributorsQueryFragment(parameters = []) {
  return `forecastContributors (horizon: $horizon ${getUsageParameters(parameters)}) {
    date
    contributors{
      ${getContributorDescriptors()}
      weightedExpectedReturn
      securityExpectedReturn
    }
  }
  `
}

export function groupedForecastContributorsQuery(forecastAndHorizon, options = {}) {
  const { isOpPro } = options ?? {}
  const forecast = get(forecastAndHorizon, "forecast")
  if (!forecast && isOpPro) { return "" }

  const includeNotHeldParameter = getIncludeNotHeldParameter(options)
  const forecastIdParameter = getForecastIdParameter(forecastAndHorizon, options)
  const forecastSecuritiesParameter = forecastIdParameter ? null : getForecastSecuritiesParameter(options)
  const analyticsOuputParameter = getAnalyticsOutputParameter(options)

  return `query ($horizon: Int! ${getQueryParameters([analyticsOuputParameter, includeNotHeldParameter, forecastSecuritiesParameter, forecastIdParameter], true)}) {
    model (id: $modelId) {
      simulation: ${getSimulateActionsNode([analyticsOuputParameter])} {
        ${groupedForecastContributorsQueryFragment([includeNotHeldParameter, forecastSecuritiesParameter, forecastIdParameter])}
      }
    }
  }`
}

export function groupedForecastContributorsQueryFragment(parameters = []) {
  return `groupedForecastContributors (horizon: $horizon groupBy: $groupBy, groupById: $groupById, classificationTier: $tier ${getUsageParameters(parameters)}) {
    date
    groupedContributors{
      id
      name
      totalExpectedReturn
      contributors{
        ${getContributorDescriptors()}
        weightedExpectedReturn
        securityExpectedReturn
      }
    }
  }
  `
}
